// src/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  addDoc,
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp 
} from 'firebase/firestore';

// Your web app's Firebase configuration
// Replace with your project's config values
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Google auth provider
const googleProvider = new GoogleAuthProvider();

// Authentication functions
const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Store user data in Firestore
      const userRef = doc(db, "users", user.uid);
      const userSnapshot = await getDoc(userRef);
      
      if (!userSnapshot.exists()) {
        // Create new user document if it doesn't exist
        console.log("Creating new user with photoURL:", user.photoURL);
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });
      } else {
        // Update user info and lastLogin timestamp
        // This ensures the photoURL is always up to date
        console.log("Updating existing user with photoURL:", user.photoURL);
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          // Update these fields in case they've changed in Google account
          displayName: user.displayName,
          photoURL: user.photoURL
        });
      }
      
      return user;
    } catch (error) {
      console.error("Error signing in with Google", error);
      throw error;
    }
  };

const logoutUser = () => {
  return signOut(auth);
};

// Referral code functions
const verifyReferralCode = async (code) => {
  try {
    // Query Firestore for the referral code
    const codesRef = collection(db, "referralCodes");
    const q = query(codesRef, where("code", "==", code), where("isUsed", "==", false));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { valid: false, message: "无效的邀请码，请重试。" };
    }
    
    // Get the first matching code
    const codeDoc = querySnapshot.docs[0];
    const codeData = codeDoc.data();
    
    // Check if code is expired
    if (codeData.expireAt && codeData.expireAt.toDate() < new Date()) {
      return { valid: false, message: "邀请码已过期，请联系我们获取新的邀请码。" };
    }
    
    return { 
      valid: true, 
      codeId: codeDoc.id,
      message: "邀请码验证成功！" 
    };
  } catch (error) {
    console.error("Error verifying referral code:", error);
    return { valid: false, message: "验证邀请码时出错，请稍后再试。" };
  }
};

const markReferralCodeAsUsed = async (codeId, userId) => {
  try {
    const codeRef = doc(db, "referralCodes", codeId);
    await updateDoc(codeRef, {
      isUsed: true,
      usedBy: userId,
      usedAt: serverTimestamp()
    });
    
    // Update user with referral code info
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      referralCode: codeId,
      referralCodeUsedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error using referral code:", error);
    return false;
  }
};

// Assessment functions
const createAssessment = async (userId, referralCodeId) => {
  try {
    const assessmentsRef = collection(db, "assessments");
    const assessmentData = {
      userId,
      referralCodeId,
      startedAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),
      lastAutoSaved: serverTimestamp(),
      completedAt: null,
      status: "in-progress",
      responses: {}
    };
    
    // Use addDoc to create a document with auto-generated ID
    const docRef = await addDoc(assessmentsRef, assessmentData);
    
    return docRef.id;
  } catch (error) {
    console.error("Error creating assessment:", error);
    throw error;
  }
};

// Function to save assessment data
const saveAssessmentData = async (assessmentId, userId, formData) => {
  try {
    const assessmentRef = doc(db, "assessments", assessmentId);
    
    // Get the current assessment to make sure it belongs to this user
    const assessmentSnap = await getDoc(assessmentRef);
    if (!assessmentSnap.exists()) {
      throw new Error("Assessment not found");
    }
    
    const assessmentData = assessmentSnap.data();
    if (assessmentData.userId !== userId) {
      throw new Error("Unauthorized to update this assessment");
    }
    
    // Update the assessment
    await updateDoc(assessmentRef, {
      responses: formData,
      lastUpdatedAt: serverTimestamp(),
      lastAutoSaved: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error saving assessment data:", error);
    throw error;
  }
};

// Function to load assessment data
const loadAssessmentData = async (userId) => {
    try {
      // Find any assessment for this user (both in-progress and completed)
      const assessmentsRef = collection(db, "assessments");
      const q = query(
        assessmentsRef, 
        where("userId", "==", userId)
        // No status filter, so we get both in-progress and completed assessments
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { exists: false };
      }
      
      // Find the most recent assessment
      let mostRecent = null;
      let mostRecentDate = new Date(0); // Jan 1, 1970
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Convert Firestore timestamp to JS Date for comparison
        const lastUpdatedAt = data.lastUpdatedAt?.toDate() || new Date(0);
        
        if (lastUpdatedAt > mostRecentDate) {
          mostRecentDate = lastUpdatedAt;
          mostRecent = { id: doc.id, ...data };
        }
      });
      
      if (!mostRecent) {
        return { exists: false };
      }
      
      return { 
        exists: true, 
        id: mostRecent.id,
        responses: mostRecent.responses || {},
        lastUpdatedAt: mostRecent.lastUpdatedAt?.toDate() || null,
        lastAutoSaved: mostRecent.lastAutoSaved?.toDate() || null,
        status: mostRecent.status || "in-progress" // Include status in the returned data
      };
    } catch (error) {
      console.error("Error loading assessment data:", error);
      throw error;
    }
};

// Function to mark an assessment as completed
const completeAssessment = async (assessmentId, userId) => {
  try {
    const assessmentRef = doc(db, "assessments", assessmentId);
    
    // Get the current assessment to make sure it belongs to this user
    const assessmentSnap = await getDoc(assessmentRef);
    if (!assessmentSnap.exists()) {
      throw new Error("Assessment not found");
    }
    
    const assessmentData = assessmentSnap.data();
    if (assessmentData.userId !== userId) {
      throw new Error("Unauthorized to update this assessment");
    }
    
    // Update the assessment
    await updateDoc(assessmentRef, {
      status: "completed",
      completedAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error completing assessment:", error);
    throw error;
  }
};

export {
  auth,
  db,
  signInWithGoogle,
  logoutUser,
  verifyReferralCode,
  markReferralCodeAsUsed,
  createAssessment,
  saveAssessmentData,
  loadAssessmentData,
  completeAssessment
};